import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface RuntimeConfig {
  api: {
    baseUrl: string;
    timeout: number;
  };
  sso: {
    issuer: string;
    clientId: string;
    redirectUri: string;
  };
  features: {
    enableDebugLogging: boolean;
    enableAnalytics: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class RuntimeConfigService {
  private config: RuntimeConfig | null = null;
  private configLoaded = false;

  constructor(private http: HttpClient) {}

  async loadConfig(): Promise<RuntimeConfig> {
    if (this.configLoaded && this.config) {
      return this.config;
    }

    try {
      // Try to load from a config file in assets
      const config = await this.http.get<RuntimeConfig>('/assets/config.json').toPromise();
      this.config = config || null;
      this.configLoaded = true;
      return this.config!;
    } catch (error) {
      console.warn('Failed to load runtime config, using defaults');
      // Fallback to default configuration
      this.config = {
        api: {
          baseUrl: 'http://localhost:8090',
          timeout: 30000
        },
        sso: {
          issuer: 'http://localsso:8443',
          clientId: 'localsso-client-id',
          redirectUri: 'http://localhost:8092'
        },
        features: {
          enableDebugLogging: true,
          enableAnalytics: false
        }
      };
      this.configLoaded = true;
      return this.config;
    }
  }

  getConfig(): RuntimeConfig | null {
    return this.config;
  }

  getApiBaseUrl(): string {
    return this.config?.api.baseUrl || 'http://localhost:8090';
  }

  getSSOIssuer(): string {
    return this.config?.sso.issuer || 'http://localsso:8443';
  }

  getSSOClientId(): string {
    return this.config?.sso.clientId || 'localsso-client-id';
  }

  getSSORedirectUri(): string {
    return this.config?.sso.redirectUri || 'http://localhost:8092';
  }

  isDebugLoggingEnabled(): boolean {
    return this.config?.features.enableDebugLogging || true;
  }

  isAnalyticsEnabled(): boolean {
    return this.config?.features.enableAnalytics || false;
  }
}
